/**
 * useUsuarios — data fetching + mutações da gestão de usuários (RF-M11-012,
 * RF-AUT-04). Usa React Query sobre o `usuariosApi` (rotas admin do M1).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useServices } from '../contexts/ServicesContext';
import {
  AtualizarUsuarioInput,
  CriarUsuarioInput,
  ListaUsuarios,
} from '../services/usuariosApi';

const CHAVE = ['usuarios'] as const;

/** Lista usuários (admin). */
export function useUsuarios(): {
  data: ListaUsuarios | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const { usuarios } = useServices();
  const query = useQuery({
    queryKey: CHAVE,
    queryFn: () => usuarios.listar(),
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError };
}

/** Convida um novo usuário; invalida a lista ao concluir. */
export function useCriarUsuario(): {
  criar: (input: CriarUsuarioInput) => Promise<void>;
  isPending: boolean;
  isError: boolean;
} {
  const { usuarios } = useServices();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: CriarUsuarioInput) => usuarios.criar(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHAVE });
    },
  });
  return {
    criar: async (input) => {
      await mutation.mutateAsync(input);
    },
    isPending: mutation.isPending,
    isError: mutation.isError,
  };
}

/** Altera perfil/status de um usuário; invalida a lista ao concluir. */
export function useAtualizarUsuario(): {
  atualizar: (id: string, input: AtualizarUsuarioInput) => Promise<void>;
  isPending: boolean;
} {
  const { usuarios } = useServices();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: AtualizarUsuarioInput }) =>
      usuarios.atualizar(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHAVE });
    },
  });
  return {
    atualizar: async (id, input) => {
      await mutation.mutateAsync({ id, input });
    },
    isPending: mutation.isPending,
  };
}

/** Ação de linha que vai ao servidor e atualiza a lista (exclusão e redefinição do autenticador, ADR-052). */
function useAcaoDeUsuario(acao: (id: string) => Promise<unknown>): {
  executar: (id: string) => Promise<void>;
  isPending: boolean;
} {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: acao,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHAVE });
    },
  });
  return {
    executar: async (id) => {
      await mutation.mutateAsync(id);
    },
    isPending: mutation.isPending,
  };
}

/** Exclui um usuário (DELETE /usuarios/{id}); invalida a lista ao concluir. */
export function useExcluirUsuario(): { executar: (id: string) => Promise<void>; isPending: boolean } {
  const { usuarios } = useServices();
  return useAcaoDeUsuario((id) => usuarios.excluir(id));
}

/** Redefine o autenticador (POST /usuarios/{id}/redefinir-mfa). */
export function useRedefinirMfa(): { executar: (id: string) => Promise<void>; isPending: boolean } {
  const { usuarios } = useServices();
  return useAcaoDeUsuario((id) => usuarios.redefinirMfa(id));
}
